import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, Subscription, timer } from 'rxjs';
import { map, finalize } from "rxjs/operators";

import { Loader, ObservableLoader } from "../models/loader.model";
import { LoaderComponent } from '../components/loader.component';

@Injectable()
export class LoaderService {
    private _defaultLoader: Loader = { showLoader: false, text: "" };
    showLoader: boolean = false;
    public _loader: LoaderComponent;
    public loader = new BehaviorSubject<Loader>(this._defaultLoader);

    private preventInfiniteLoader: Observable<any> = timer(720000).pipe(map(() => {
        console.log('timer expired');
        let forceDisable: boolean = false;
        if (this.loader != null && this.loader.value.showLoader == true) {
            forceDisable = true;
            this.loader.next(this._defaultLoader);
        }
        return forceDisable;
    })); //7 minutes to disable loader if it hasn't stopped before then
	private preventInfiniteSubscription: Subscription;

    // Set to true to re-enable the random fun-fact text instead of the static "please wait" message.
    public _useRandomText: boolean = false;
    public _defaultText: string = "Page loading, please wait";

    public _randomTexts: string[] = [
        `The first well in America that produced oil was drilled in 1814 in Noble County, Ohio.It
        was drilled for salt - brine to preserve meat. The oil was an unwanted product that the
        settlers soaked up with blankets to remove from the water.`,
        `Oil and gas have been produced commercially in Ohio for over 150 years or since 1860.`,
        `Oil and gas wells used to be plugged with cast-iron balls, tree trunks, and drill cuttings.
        Today, wells are plugged with cement placed in the wellbore across specific zones
        according to regulations.`,
        `Ohio has 89 seismic monitoring stations that track earthquakes and seismic activity in
        real time.`,
        `Refining one barrel (42 gallons) of oil produces approximately 33 gallons of gasoline.`,
        `Created in 1965, the Division of Oil and Gas Resources Management is responsible for
        regulating Ohio's oil and natural gas industry for the protection of all Ohioans and our
        environment while ensuring the state's abundant natural resources are managed
        properly.`,
        `Ohio is home to much of the Utica and Marcellus shale plays, which have accounted for
        85 percent of the U.S. shale gas production growth since the start of 2011. These shale
        plays contain large amounts of natural gas, natural gas liquids and crude oil. Ohio's
        availability of these plentiful and low-cost feedstocks, combined with its proximity to
        end markets, make the state the best option for midstream and downstream
        investments.`,
        `The Cleveland State University study reveals that shale-related investment in Ohio
        totaled an estimated $63.9 billion from 2011 through June 2017.`,
        `Ohio is home to the first offshore drilling operation. An oil well was drilled from a barge
        in Grand Lake St. Marys' (Mercer County) in the late 1800's.`,
        `In 1888, Ohio's first environmental standard for the oil and gas industry (Law 286)
        prohibited the discharge of crude oil, petroleum, refined oil, refuse or filth from an oil
        well into a ditch, drain or water course.`,
        `The first well construction and plugging standards in Ohio were enacted in 1893 to
        ensure that oil wells were constructed in a manner that prevented oil and gas bearing
        zones from being contaminated by freshwater.`,
        `In 1977, the Ohio General Assembly established the Idle and Orphaned Well Program
        funded by a portion of oil and gas severance tax, to plug and restore well sites with no
        legally responsible owner.`,
        `In 1983, the United States Environmental Protection Agency awarded Ohio primary
        enforcement authority to permit and regulate Class II injection wells used to dispose
        brine, the saline water by-product of oil and gas production, into deep saline reservoirs.`,
        `Hydraulic fracturing is an engineered, extensively monitored process performed to
        create and preserve a fracture system that connects a well to a surrounding porous
        medium, including but not limited to natural gas reservoirs.`,
        `Hydraulic fracturing is not new technology. It has been used since the late 1940s to
        stimulate oil and gas production in the United States.`,
        `The first use of hydraulic fracturing to stimulate an Ohio oil and gas well occurred near
        East Sparta (Stark County) in 1952.`,
        `According to the United States Department of Energy shale gas development is one of
        the most water efficient energy resources.`,
        `Ohio was the leading oil and gas producing state in the United States for eight
        consecutive years from 1895 -1902 when John D. Rockefeller created the Standard Oil
        Company. Many of the largest oil companies in the world today (Marathon, Exxon/Mobil, Chevron and British Petroleum) were derived from the court ordered break-up
        of his oil monopoly.`,
        `The all-time record for drilling activity occurred in 1899, when 6,807 wells were drilled,
        primarily in the Trenton Limestone Lima-Findlay field in northwestern Ohio.`,
        `Historically there have been approximately 280,000 wells drilled. Of those, over 81,000
        have been drilled to the Clinton sandstone making it the most actively drilled formation.`,
        `Through August 2019, over 2,300 Utica/Point Pleasant horizontal wells are in
        production.`,
        `In 2018 in Monroe County, a horizontal well was drilled 30,676 feet into the Point
        Pleasant Shale, making it the longest well ever drilled in Ohio. Compared to 2011, when
        the longest well in Ohio was 14,445 feet.`,
        `In 2015, Ohio's total crude oil production was 26,781,178 barrels, the most oil ever
         in a year; the previous record for oil production was 23,941,169 barrels in
         1896.`,
        `Through 2018, Ohio wells have produced 1,260,226,516 barrels of oil.`,
        `In 2018, Ohio wells produced 2,398,386,763 mcf of natural gas, the highest amount ever
        produced in a year.`,
        `Through 2018, Ohio wells have cumulatively produced 15,975,790,023 mcf of natural
        gas.`,
        `In 2018, Ohio's combined oil and gas market value was $8,374,209,575, the highest on
        record.`,
        `Historically through 2012, Ohio's annual natural gas production had never provided
        more than 10% of Ohio's consumption; in 2018 natural gas production provided 249% of
        natural gas consumption.`,
        `The highest average price for natural gas was $9.77 per mcf in 2008.`,
        `The highest average price for crude oil was $95.71 per barrel in 2013.`,
    ];

    queueObservables(items: ObservableLoader[], simCity: boolean = true): Observable<any> {
        if (simCity) {
            items.forEach(x => {
                if (!x.text)
                    x.text = this._getRandomText();
            });
        }

        return this._loader.load(items);
    }

    public _getRandomText() {
        if (!this._useRandomText)
            return this._defaultText;

        let random = Math.floor(Math.random() * this._randomTexts.length);

        return this._randomTexts[random];
    }

    private pendingEvents: number = 0;

    reset(): void {
        if (this.preventInfiniteSubscription != null) this.preventInfiniteSubscription.unsubscribe();
        this.pendingEvents = 0;
        this.loader.next(this._defaultLoader);
    }

    toggleLoader(visible, text?: string, simCity: boolean = true): void {
        if (!text && simCity)
            text = this._getRandomText();
        if (visible) this.pendingEvents += 1;
        else this.pendingEvents -= 1;
        if (visible && this.pendingEvents > 0) {
            this.loader.next({ showLoader: true, text: text });
            if (this.preventInfiniteSubscription != null) this.preventInfiniteSubscription.unsubscribe(); //disconnect from any previous infinite limits and restart
            this.preventInfiniteSubscription = this.preventInfiniteLoader.pipe(
                finalize(() => { this.preventInfiniteSubscription.unsubscribe(); }))
                .subscribe(
                    (forced: boolean) => {
                        if (forced) console.log("disabling loader to prevent infinite spin");
                    },
                    (error) => { console.error(error); }
                );
        }
        else if (!visible && this.pendingEvents <= 0) {
            this.loader.next({ showLoader: false, text: text });
            this.pendingEvents = 0;
        }
    }

    getLoader(): Observable<any> {
        return this.loader.asObservable();
    }

    register(component: LoaderComponent) {
        this._loader = component;
    }
}